import { Steps } from '@starsoft/common/components';
import { useKycNavigation } from '@starsoft/common/hooks';
import { useCoallescedCoinId } from '../../../../../../hooks/coin/useCoallescedCoinId';
import { KycCheckpointStatus } from '../../../../../../enums/kyc/checkpoint/status';
import { KycCheckpointIdType } from '@starsoft/common/enums/kyc/checkpoint';

/**
 * KYC User Identity Form Content Steps Component
 * Renders a stepper component that shows the progress of KYC verification steps.
 * The component handles navigation between different KYC steps and blocks progression
 * until the current step is verified.
 *
 * @component
 * @returns {JSX.Element|null} Steps component or null if less than 2 steps
 *
 * @example
 * // Basic usage
 * <KYCUserIdentityFormContentSteps />
 *
 * // The component internally uses:
 * useKycNavigation({
 *   coinId: "1" // string | undefined
 * });
 *
 * // And renders Steps component with:
 * <Steps
 *   currentIndex={0}
 *   currentStep="IDENTITY"
 *   options={[
 *     { id: "IDENTITY", label: "Identity" },
 *     { id: "DOCUMENT", label: "Document" }
 *   ]}
 *   onStepClick={(step: string) => void}
 * />
 */
export default function KYCUserIdentityFormContentSteps() {
  const { coinId } = useCoallescedCoinId();

  const {
    kycCheckpointId,
    currentStepIndex,
    handleSetKycStep,
    parsedSteps,
    kycMeByOriginIdQuery: { isPending },
    currentCheckpoint,
  } = useKycNavigation({
    coinId: coinId?.toString(),
  });

  if (!parsedSteps || parsedSteps?.length < 2) {
    return null;
  }

  return (
    <Steps
      currentIndex={currentStepIndex}
      currentStep={kycCheckpointId as KycCheckpointIdType}
      i18nWorkspace="common"
      isLoading={isPending}
      blockFurtherSteps={
        currentCheckpoint?.status !== KycCheckpointStatus.Verified
      }
      options={parsedSteps}
      onStepClick={handleSetKycStep}
    />
  );
}
